"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import axios from "axios"
import Sidebar from "./Sidebar"
import Header from "./Header"
import { FiArrowRight, FiUser, FiMail, FiLock, FiRefreshCw } from "react-icons/fi"

const generatePassword = () => {
  const length = 12
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+"
  let password = ""
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length))
  }
  return password
}

function AddUsers() {
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 1024 : true))
  const [formData, setFormData] = useState({
    full_name: "",
    username: "",
    password: "",
    email: "",
    phone: "",
    company_id: "",
    role: "",
    billing_account_id: "",
  })
  const [companies, setCompanies] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [notification, setNotification] = useState(null)
  const [filteredOptions, setFilteredOptions] = useState([])
  const [selectedRole, setSelectedRole] = useState(null)
  const [userRole, setUserRole] = useState("")
  const [userCompany, setUserCompany] = useState({ company_id: "", company_name: "", billing_account_id: "" })

  // Role options
  const roleOptions = [
    { value: "Admin", label: "Admin" },
    { value: "Customer Admin", label: "Customer Admin" },
    { value: "Customer", label: "Customer" },
  ]

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: "1px solid #d1d5db",
      borderRadius: "0.5rem",
      padding: "0.5rem",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.5)" : "none",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      "&:hover": {
        borderColor: "#9ca3af",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#eff6ff" : "white",
      color: state.isSelected ? "white" : "#374151",
      padding: "0.75rem",
    }),
  }

  // Fetch companies data
  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoading(true)
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/customers/`)
        const companyOptions = response.data.map((company) => ({
          value: company.company_id,
          label: company.company_name,
          billing_account_id: company.billing_account_id,
        }))
        setCompanies(companyOptions)
      } catch (error) {
        console.error("Error fetching companies:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchCompanies()
  }, [])

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"))
    const role = user ? user.role : ""
    setUserRole(role)
    if (role !== "Admin" && user) {
      setUserCompany({
        company_id: user.company_id,
        company_name: user.company_name,
        billing_account_id: user.billing_account_id,
      })
      setFormData((prevData) => ({
        ...prevData,
        company_id: user.company_id,
        billing_account_id: user.billing_account_id,
      }))
    }
  }, [])

  const toggleSidebar = () => {
    setSidebarOpen((prevState) => !prevState)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleSelectChange = (selectedOption) => {
    setFormData((prevData) => ({
      ...prevData,
      company_id: selectedOption ? selectedOption.value : "",
      billing_account_id: selectedOption ? selectedOption.billing_account_id : "",
    }))
  }

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"))
    const role = user ? user.role : ""

    if (role === "Customer Admin") {
      setFilteredOptions(
        roleOptions.filter((option) => option.value === "Customer" || option.value === "Customer Admin"),
      )
    } else {
      setFilteredOptions(roleOptions)
    }
  }, [])

  const handleRoleChange = (selectedOption) => {
    setSelectedRole(selectedOption)
    setFormData((prevData) => ({
      ...prevData,
      role: selectedOption ? selectedOption.value : "",
    }))
  }

  const handlePasswordGenerate = () => {
    const newPassword = generatePassword()
    setFormData((prevData) => ({
      ...prevData,
      password: newPassword,
    }))
  }

    const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/`, {
        full_name: formData.full_name,
        username: formData.username,
        password: formData.password,
        email: formData.email,
        phone: formData.phone,
        company_id: formData.company_id,
        role: formData.role,
        billing_account_id: formData.billing_account_id,
      })

      setNotification({
        type: "success",
        message: "User added successfully!",
      })

      // Reset form
      setFormData({
        full_name: "",
        username: "",
        password: "",
        email: "",
        phone: "",
        company_id: "",
        role: "",
        billing_account_id: "",
      })
      setSelectedRole(null)

      setTimeout(() => {
        navigate("/user-management")
      }, 1000) 
    } catch (error) {
      console.error("Error adding user:", error)
      setNotification({
        type: "error",
        message: "Failed to add user. Please try again.",
      })
    }
  }

  const handleCloseNotification = () => {
    setNotification(null)
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

      <div className="flex flex-1 bg-gray-100 min-w-0 max-w-full overflow-x-hidden">
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

        <div className={`flex-1 min-w-0 max-w-full overflow-x-hidden p-3 sm:p-6 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "ml-0"} ml-0`}>
          <div className="pt-20 pb-6">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Add New User</h1>
                <p className="text-gray-600 mt-1">Create a new user account with the required information</p>
              </div>

              {/* Form Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      {/* Personal Information Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <FiUser className="mr-2" />
                          Personal Information
                        </h3>

                        <div className="space-y-3">
                          {/* Full Name */}
                          <div>
                            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                              Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="fullName"
                              name="full_name"
                              value={formData.full_name}
                              onChange={handleInputChange}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                              placeholder="Enter full name"
                              required
                            />
                          </div>

                          {/* Username */}
                          <div>
                            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                              Username <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="username"
                              name="username"
                              value={formData.username}
                              onChange={handleInputChange}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                              placeholder="Enter username"
                              required
                            />
                          </div>

                          {/* Password */}
                          <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                              Password <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                placeholder="Enter password"
                                required
                              />
                              <button
                                type="button"
                                onClick={handlePasswordGenerate}
                                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                                title="Generate Password"
                              >
                                <FiRefreshCw className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Contact Information Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <FiMail className="mr-2" />
                          Contact Information
                        </h3>

                        <div className="space-y-3">
                          {/* Email */}
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                              Email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                              placeholder="Enter email address"
                              required
                            />
                          </div>

                          {/* Phone */}
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                              Phone <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                              placeholder="Enter phone number"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      {/* Role & Permissions Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <FiLock className="mr-2" />
                          Role & Permissions
                        </h3>

                        <div>
                          <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                            Role <span className="text-red-500">*</span>
                          </label>
                          <Select
                            id="role"
                            name="role"
                            options={filteredOptions}
                            value={selectedRole}
                            onChange={handleRoleChange}
                            placeholder="Select a role"
                            styles={customSelectStyles}
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* Company & Billing Section */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Company & Billing</h3>

                        <div className="space-y-3">
                          {/* Company Name */}
                          <div>
                            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                              Company Name <span className="text-red-500">*</span>
                            </label>
                            {userRole !== "Admin" ? (
                              <input
                                type="text"
                                id="companyName"
                                name="company_name"
                                value={userCompany.company_name}
                                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 focus:outline-none"
                                readOnly
                              />
                            ) : (
                              <Select
                                id="companyName"
                                name="company_id"
                                options={companies}
                                value={companies.find((option) => option.value === formData.company_id)}
                                onChange={handleSelectChange}
                                isLoading={isLoading}
                                placeholder="Select a company"
                                styles={customSelectStyles}
                                className="w-full"
                              />
                            )}
                          </div>

                          {/* Billing ID */}
                          <div>
                            <label htmlFor="billingID" className="block text-sm font-medium text-gray-700 mb-1">
                              Billing Account ID
                            </label>
                            <input
                              type="text"
                              id="billingID"
                              name="billing_account_id"
                              value={userRole !== "Admin" ? userCompany.billing_account_id : formData.billing_account_id}
                              onChange={handleInputChange}
                              className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 focus:outline-none"
                              placeholder="Auto-filled when company is selected"
                              readOnly
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              This field is automatically populated when you select a company
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      className="flex items-center bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 px-6 rounded-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 ease-in-out transform hover:scale-105"
                    >
                      <FiUser className="mr-2 w-4 h-4" />
                      Add User
                      <FiArrowRight className="ml-2 w-4 h-4" />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      {notification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div
                className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  notification.type === "success" ? "bg-green-100" : "bg-red-100"
                }`}
              >
                {notification.type === "success" ? (
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <h3
                className={`text-lg font-semibold mb-2 ${
                  notification.type === "success" ? "text-green-900" : "text-red-900"
                }`}
              >
                {notification.type === "success" ? "Success!" : "Error"}
              </h3>
              <p className="text-gray-600 mb-6">{notification.message}</p>
              <button
                onClick={handleCloseNotification}
                className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AddUsers
